import { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { auth, ApiError, RateLimitError } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { PasswordField } from '../components/auth/PasswordField';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { RateLimitInline, isRateLimited } from '../components/RateLimitBanner';
import { usePageTitle } from '../lib/usePageTitle';
import { safeNextPath } from '../lib/safeNextPath';

export function SignInPage() {
  usePageTitle('QuantumHire - Sign In');
  const navigate = useNavigate();
  const { setCandidate } = useAuth();
  // Recruitment workflow step 5c — `?next=` returns the candidate where they came from (the
  // invite page sends them back to the job). `?return=` is an alias (step 5e): the job page,
  // dashboard and change-password have always sent it, but it was never honoured. Same-site
  // paths only; otherwise home. The invite page passes the email in router state, never the
  // URL (page views are sent to analytics).
  const [params] = useSearchParams();
  const location = useLocation();
  const nextPath = safeNextPath(params.get('next')) ?? safeNextPath(params.get('return')) ?? '/';
  const prefillEmail = (location.state as { email?: unknown } | null)?.email;
  const [email, setEmail] = useState(typeof prefillEmail === 'string' ? prefillEmail : '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password || isRateLimited(rateLimitedUntil)) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await auth.signin({ email: email.trim(), password });
      if (result.kind === 'recoverable') {
        // Soft-deleted account — backend minted a recovery cookie. The dedicated page
        // calls POST /candidates/me/recover to consume it on user confirm.
        navigate('/recover-account', {
          state: {
            deleted_at: result.deleted_at,
            days_in_soft_delete: result.days_in_soft_delete,
          },
          replace: true,
        });
        return;
      }
      setCandidate(result.candidate);
      navigate(nextPath);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not sign you in. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = useCallback(
    async (idToken: string) => {
      setError(null);
      setSubmitting(true);
      try {
        const me = await auth.signinWithGoogle({ id_token: idToken });
        setCandidate(me);
        navigate(nextPath);
      } catch (err) {
        if (err instanceof RateLimitError) {
          setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Could not sign you in with Google. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, setCandidate, nextPath],
  );

  return (
    <AuthCard
      publicTheme
      eyebrow="Candidate sign-in"
      title="Welcome back"
      subtitle="Sign in to apply to roles, manage your profile, and track applications."
      footer={
        <>
          New to QuantumHire?{' '}
          <Link
            to="/signup"
            className="text-brand-primary font-bold hover:text-brand-accent transition-colors"
          >
            Create an account
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />
      <RateLimitInline
        deadlineMs={rateLimitedUntil}
        className="text-sm text-amber-900 font-medium mb-3"
      />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            type="email"
            required
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium"
          />
        </div>

        <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />

        <div className="flex justify-end -mt-1">
          <Link
            to="/forgot-password"
            className="text-xs font-bold text-brand-muted hover:text-brand-primary transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={submitting || !email.trim() || !password || isRateLimited(rateLimitedUntil)}
          className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>

      <GoogleSignInButton onCredential={handleGoogle} text="signin_with" disabled={submitting} />
    </AuthCard>
  );
}
